import React from 'react'
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap'
import { DateTime } from 'luxon'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import {
	Client,
	Invoice,
	WipClientWithJobsAndLatestInvoice,
	WipData,
	WipTimeWithoutJob,
	XeroAccount,
	XeroTaxRate,
} from '../../../../back-end/utilities/apiDefinitions'

import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom'
import ListingScreen from '../../components/invoice/listingScreen'
import { InvoiceTime } from '../../../../back-end/common/invoiceTime'
import { Estimate } from '../../../../back-end/common/estimate'
import { roundToTwo } from '../../utilities/format'
import { PageStatus } from '../../constants/pageStatus'
import { Loading } from '../../components/ui/Loading/Loading'
import { InvoiceLineAmountTypes } from '../../../../back-end/common/invoice'
import { FormNumber } from '../../components/ui/Form/Number'

import './invoiceAllocation.css'
import InvoiceInfoCard from '../../components/invoice/invoiceInfoCard'
import { ChevronDown, ChevronUp, RefreshCw } from 'react-feather'
import { Grouping } from '../../constants/grouping'
import { useStateParams } from '../../utilities/hooks'
import OtherJobPanel from '../../components/invoice/otherJobPanel'
import { InvoiceEstimate } from '../../../../back-end/common/invoiceEstimate'
import { InvoiceCost } from '../../../../back-end/common/invoiceCost'
import NewEstimateModal, { UpdateableEstimate } from '../../components/invoice/newEstimateModal'
import { invoiceState } from '../../utilities/invoice'
import { getTaxRateFromType } from '../../utilities/taxRates'
import { FormText } from '../../components/ui/Form/Text'

const groupingOptions = Object.values(Grouping)

type InvoiceTimeData = (Pick<InvoiceTime, 'invoiceID' | 'timeID' | 'amountBilled' | 'writtenOff'> &
	Partial<Pick<InvoiceTime, 'invoiceTimeID' | 'timeJobXpmID'>>)[]
type InvoiceEstimateData = (Pick<InvoiceEstimate, 'invoiceID' | 'estimateID' | 'amountBilled' | 'writtenOff'> &
	Partial<Pick<InvoiceEstimate, 'invoiceEstimateID'>>)[]
type InvoiceCostData = (Pick<InvoiceCost, 'invoiceID' | 'costID' | 'amountBilled' | 'writtenOff'> &
	Partial<Pick<InvoiceCost, 'invoiceCostID' | 'costJobXpmID'>>)[]
export type RowTypes = 'cost' | 'estimateGroup' | 'estimate' | 'jobType' | 'client' | 'job' | 'time'

const newEstimate = (invoiceID: string): UpdateableEstimate => ({
	estimateID: '',
	value: 0,
	description: '',
	date: DateTime.utc().minus({ month: 1 }).endOf('month').toJSON().slice(0, 10),
	originalInvoiceID: invoiceID,
	clientID: '',
})

const InvoiceDetail = () => {
	const location = useLocation()
	const navigate = useNavigate()
	const { appState } = React.useContext(AppContext)
	const [pageStatus, setPageStatus] = React.useState<PageStatus>('Loading')
	const [showJobModal, setShowJobModal] = React.useState<boolean>(false)
	const [estimateModalEstimate, setEstimateModalEstimate] = React.useState<UpdateableEstimate | null>(null)
	const [somethingChanged, setSomethingChanged] = React.useState<boolean>(false)

	const [invoice, setInvoice] = React.useState<Invoice | null>(null)
	const [newEstimates, setNewEstimates] = React.useState<Estimate[] | null>(null)
	const [client, setClient] = React.useState<Client | null>(null)
	const [xeroAccounts, setXeroAccounts] = React.useState<XeroAccount[] | null>(null)
	const [taxRates, setTaxRates] = React.useState<XeroTaxRate[] | null>(null)

	const [invoiceTimeData, setInvoiceTimeData] = React.useState<InvoiceTimeData | null>(null)
	const [invoiceEstimateData, setInvoiceEstimateData] = React.useState<InvoiceEstimateData | null>(null)
	const [invoiceCostData, setInvoiceCostData] = React.useState<InvoiceCostData | null>(null)
	const [wipData, setWipData] = React.useState<WipData | null>(null)

	const [message, setMessage] = React.useState<string>('')

	const match = useMatch('/invoice/:id/allocation')
	const invoiceId = match?.params.id || ''

	const currentAccountBillySettings = appState.accounts.find(
		(acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId']
	)?.billySettings
	const currentOrgBillySettings = currentAccountBillySettings?.orgSettings.find((os) => os.xeroOrgID === invoice?.xeroOrgID)
	const [restrictionDate, setRestrictionDate] = React.useState<string>(DateTime.utc().toFormat('yyyy-MM-dd'))
	const [grouping, setGrouping] = useStateParams<Grouping>(
		Grouping.fcje,
		'grouping',
		(d) => d.value,
		(d) => Grouping[d as keyof typeof Grouping]
	)
	const [showGroupingPanel, setShowGroupingPanel] = React.useState(false)

	React.useEffect(() => {
		if (invoice) {
			setRestrictionDate(DateTime.fromISO(invoice.date).toFormat('yyyy-MM-dd'))
		}
	}, [invoice])

	const getWipData = React.useCallback(
		async (invoice: Invoice, invoiceTimes: InvoiceTimeData, invoiceCosts: InvoiceCostData, invoiceEstimates: InvoiceEstimateData) => {
			const jobs = [
				...new Set([
					...invoiceTimes
						.reduce((jobs, invoiceTimes) => (invoiceTimes.timeJobXpmID ? [...jobs, invoiceTimes.timeJobXpmID] : jobs), [] as string[])
						.concat(
							invoiceCosts.reduce(
								(jobs, invoiceCosts) => (invoiceCosts.costJobXpmID ? [...jobs, invoiceCosts.costJobXpmID] : jobs),
								[] as string[]
							)
						),
				]),
			] // get all unique jobs of times and costs associated with this invoice
			const estimates = invoiceEstimates.reduce((estimates, invoiceEstimates) => [...estimates, invoiceEstimates.estimateID], [] as string[])
			const clientColumn = (operator: string) =>
				invoice.clientGroupID ? `ClientGroupID${operator}${invoice.clientGroupID}` : `ClientID${operator}${invoice.clientID}`
			const [clientWipRes, jobWipRes, estimateWipRes] = await Promise.all([
				Request.get(`wip/${grouping.value}?where=${clientColumn('==')}&invoiceContext=${invoiceId}`),
				jobs.length > 0
					? Request.get(`wip/${grouping.value}?where=${clientColumn('!=')}&where=JobXpmID=in(${jobs.join(',')})&invoiceContext=${invoiceId}`)
					: Promise.resolve({ data: { wipData: { grouping: grouping.value, familyGroups: [] } } }),
				estimates.length > 0
					? Request.get(`wip/${grouping.value}?where=${clientColumn('!=')}&where=EstimateID=in(${estimates.join(',')})&invoiceContext=${invoiceId}`)
					: Promise.resolve({ data: { wipData: { grouping: grouping.value, familyGroups: [] } } }),
			])

			setWipData(mergeWipData([clientWipRes.data.wipData, jobWipRes.data.wipData, estimateWipRes.data.wipData]))
			setPageStatus('Ready')
		},
		[invoiceId, grouping]
	)
	const getJobWipData = React.useCallback(
		async (jobIDs: string[]) => {
			const jobWipRes = await Request.get(`wip/${grouping.value}?where=JobID=in(${jobIDs.join(',')})`)
			const newWipData = mergeWipData([wipData, jobWipRes.data.wipData])
			setWipData(newWipData)
			setPageStatus('Ready')
		},
		[grouping, wipData]
	)

	const getData = async () => {
		const [invoiceRes, invoiceTimeRes, invoiceEstimateRes, invoiceCostRes, estimateRes] = await Promise.all([
			Request.get(`invoice?where=InvoiceID==${invoiceId}`),
			Request.get(`invoiceTime?where=InvoiceID==${invoiceId}`),
			Request.get(`invoiceEstimate?where=InvoiceID==${invoiceId}`),
			Request.get(`invoiceCost?where=InvoiceID==${invoiceId}`),
			Request.get(`estimate?where=EstimateOriginalInvoiceID==${invoiceId}`),
		])
		const invoice: Invoice = invoiceRes.data.invoices[0]
		const invoiceTimes = invoiceTimeRes.data.invoiceTimes
		const invoiceEstimates = invoiceEstimateRes.data.invoiceEstimates
		const invoiceCosts = invoiceCostRes.data.invoiceCosts
		const estimates = estimateRes.data.estimates
		setInvoice(invoice)
		setInvoiceTimeData(invoiceTimes)
		setInvoiceEstimateData(invoiceEstimates)
		setInvoiceCostData(invoiceCosts)
		setNewEstimates(estimates)

		getWipData(invoice, invoiceTimes, invoiceCosts, invoiceEstimates)

		const [clientRes, accountsRes, taxRatesRes] = await Promise.all([
			Request.get(`client?where=ClientID==${invoice.clientID}`),
			Request.get(`xeroaccount?where=XeroOrgID==${invoice.xeroOrgID}`),
			Request.get(`xerotaxrate?where=XeroOrgID==${invoice.xeroOrgID}`),
		])
		setClient(clientRes.data.clients[0])
		setXeroAccounts(accountsRes.data.xeroAccounts)
		setTaxRates(taxRatesRes.data.xeroTaxRates)
	}

	React.useEffect(() => {
		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState])

	const agedWipTotal = React.useMemo(
		() =>
			roundToTwo(
				getAllTimes((wipData || { familyGroups: [] }) as WipData).reduce(
					(total, time) => (total += isDateBefore(time.date, restrictionDate) && (time.timeID || time.costID) ? time.agedValue : 0),
					0
				)
			),
		[wipData, restrictionDate]
	)
	const currentWipTotal = React.useMemo(
		() =>
			roundToTwo(
				getAllTimes((wipData || { familyGroups: [] }) as WipData).reduce(
					(total, time) => (total += isDateBefore(time.date, restrictionDate) && (time.timeID || time.costID) ? time.currentValue : 0),
					0
				)
			),
		[wipData, restrictionDate]
	)
	const wipTotal = React.useMemo(
		() =>
			roundToTwo(
				getAllTimes((wipData || { familyGroups: [] }) as WipData).reduce(
					(total, time) => (total += isDateBefore(time.date, restrictionDate) && (time.timeID || time.costID) ? time.remainingDollars : 0),
					0
				) || 0
			),
		[wipData, restrictionDate]
	)
	const woTotal = React.useMemo(
		() =>
			roundToTwo(
				(invoiceTimeData?.reduce((total, invoiceTime) => (total += invoiceTime.writtenOff), 0) || 0) +
					(invoiceCostData?.reduce((total, invoiceCost) => (total += invoiceCost.writtenOff), 0) || 0) +
					(invoiceEstimateData?.reduce((total, invoiceEstimate) => (total += invoiceEstimate.writtenOff), 0) || 0)
			),
		[invoiceTimeData, invoiceEstimateData, invoiceCostData]
	)
	const invTotal = React.useMemo(
		() =>
			roundToTwo(
				(invoiceTimeData?.reduce((total, invoiceTime) => (total += invoiceTime.amountBilled), 0) || 0) +
					(invoiceCostData?.reduce((total, invoiceCost) => (total += invoiceCost.amountBilled), 0) || 0)
			),
		[invoiceTimeData, invoiceEstimateData, invoiceCostData]
	)
	const invEstimateTotal = React.useMemo(
		() => roundToTwo(invoiceEstimateData?.reduce((total, invoiceEstimate) => (total += invoiceEstimate.amountBilled), 0) || 0),
		[invoiceEstimateData]
	)
	const newEstimateTotal = React.useMemo(
		() => (newEstimates || []).filter((e) => e.originalInvoiceID === invoiceId).reduce((total, estimate) => (total += estimate.value), 0) || 0,
		[newEstimates]
	)
	const [carryForwardTotal, writtenUpTotal] = React.useMemo(() => {
		const [carryForward, writeUp] = getAllTimes((wipData || { familyGroups: [] }) as WipData)
			.filter((e) => isDateBefore(e.date, restrictionDate))
			.reduce(
				([carryForward, writeUp], time) => {
					const invoiceTimeRecord = invoiceTimeData?.find((it) => it.timeID === time.timeID)
					const invoiceCostRecord = invoiceCostData?.find((ic) => ic.costID === time.costID)
					const invoiceEstmateTime = invoiceEstimateData?.find((ie) => ie.estimateID === time.estimateID)
					const amountBilled = invoiceTimeRecord?.amountBilled || invoiceCostRecord?.amountBilled || invoiceEstmateTime?.amountBilled
					const writtenOff = invoiceTimeRecord?.writtenOff || invoiceCostRecord?.writtenOff || invoiceEstmateTime?.writtenOff
					const totalInvoiced = time.estimateID
						? (time.remainingDollars - (amountBilled || 0) - (writtenOff || 0)) * -1
						: Math.max(0, time.remainingDollars - (amountBilled || 0) - (writtenOff || 0))
					const totalWrittenUp = time.estimateID ? 0 : Math.max(0, (amountBilled || 0) + (writtenOff || 0) - time.remainingDollars)
					return [(carryForward += totalInvoiced), (writeUp += totalWrittenUp)]
				},
				[0, 0]
			) || [0, 0]
		return [roundToTwo(carryForward), roundToTwo(writeUp)]
	}, [wipData, invoiceTimeData, invoiceCostData, invoiceEstimateData, restrictionDate])

	if (pageStatus === 'Loading') {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	if (pageStatus === 'Error' || invoice === null) {
		return (
			<div className="center-flex flex-column p-5">
				<h3>Error loading invoice</h3>
				<Button
					variant="primary"
					onClick={() => {
						navigate('/invoices')
					}}
				>
					Back to Invoices
				</Button>
			</div>
		)
	}

	const handleUpdateInvoiceTime = (type: RowTypes, id: string, field: 'writeOff' | 'invoice', value: number) => {
		setSomethingChanged(true)
		if (['jobType', 'client', 'job', 'time', 'cost'].includes(type) && invoiceTimeData && wipData && invoiceCostData) {
			const key: keyof InvoiceTime = field === 'writeOff' ? 'writtenOff' : 'amountBilled'
			let times: WipTimeWithoutJob[] = []
			if (type === 'jobType') {
				times = getAllTimesForJobTypeInWip(wipData, id, ['time', 'cost'], restrictionDate)
			}
			if (type === 'client') {
				// if job, get all times for that client, (create new ones if needed, update existing ones if exist) and split value evenly between those
				times = getAllTimesForClientInWip(wipData, id, ['time', 'cost'], restrictionDate)
			}
			if (type === 'job') {
				// if job, get all times for that job, (create new ones if needed, update existing ones if exist) and split value evenly between those
				times = getAllTimesForJobInWip(wipData, id, ['time', 'cost'], restrictionDate)
			}
			if (type === 'time' || type === 'cost') {
				// if time, look through invoice time, if one exists update it, if not make a new one
				times = getTimeInWip(wipData, id, ['time', 'cost'], restrictionDate)
			}

			const totalTimeValue = times.reduce((total, time) => total + time.remainingDollars, 0)
			const fractionOfTotal = value / totalTimeValue
			const totalFractionOfTimeValue = roundToTwo(totalTimeValue * fractionOfTotal)

			// for each time look for existing time, update existing or create new
			const updatedInvoiceTimes: InvoiceTimeData = JSON.parse(JSON.stringify(invoiceTimeData)) // deep copy
			const updatedInvoiceCosts: InvoiceCostData = JSON.parse(JSON.stringify(invoiceCostData)) // deep copy

			let ongoingTotal = 0
			for (let i = 0; i < times.length; i++) {
				const timeID = times[i].timeID
				const costID = times[i].costID
				if (timeID) {
					let timeValue = roundToTwo(times[i].remainingDollars * fractionOfTotal)
					ongoingTotal += timeValue
					// on final value only, if we are a little bit short of where we should be add the difference
					if (ongoingTotal !== totalFractionOfTimeValue && i + 1 === times.length) {
						timeValue += totalFractionOfTimeValue - ongoingTotal
					}

					const invoiceTimeRecordIndex = updatedInvoiceTimes.findIndex((it) => it.timeID === timeID)

					if (invoiceTimeRecordIndex >= 0) {
						updatedInvoiceTimes[invoiceTimeRecordIndex] = { ...updatedInvoiceTimes[invoiceTimeRecordIndex], [key]: timeValue }
					} else {
						updatedInvoiceTimes.push({ invoiceID: invoiceId, timeID: timeID, writtenOff: 0, amountBilled: 0, [key]: timeValue })
					}
				} else if (costID) {
					let costValue = roundToTwo(times[i].remainingDollars * fractionOfTotal)
					ongoingTotal += costValue
					// on final value only, if we are a little bit short of where we should be add the difference
					if (ongoingTotal !== totalFractionOfTimeValue && i + 1 === times.length) {
						costValue += totalFractionOfTimeValue - ongoingTotal
					}

					const invoiceCostRecordIndex = updatedInvoiceCosts.findIndex((ic) => ic.costID === costID)

					if (invoiceCostRecordIndex >= 0) {
						updatedInvoiceCosts[invoiceCostRecordIndex] = { ...updatedInvoiceCosts[invoiceCostRecordIndex], [key]: costValue }
					} else {
						updatedInvoiceCosts.push({ invoiceID: invoiceId, costID: costID, writtenOff: 0, amountBilled: 0, [key]: costValue })
					}
				}
			}

			setInvoiceTimeData(updatedInvoiceTimes)
			setInvoiceCostData(updatedInvoiceCosts)
		} else if (['estimateGroup', 'estimate'].includes(type) && invoiceEstimateData && wipData) {
			const key: keyof InvoiceEstimate = field === 'writeOff' ? 'writtenOff' : 'amountBilled'
			let updateEstimates: WipTimeWithoutJob[] = []
			if (type === 'estimateGroup') {
				updateEstimates = getAllTimesForJobInWip(wipData, id, ['estimate'], restrictionDate)
			}
			if (type === 'estimate') {
				updateEstimates = getTimeInWip(wipData, id, ['estimate'], restrictionDate)
			}

			const totalEstimateValue = updateEstimates.reduce((total, time) => total + time.remainingDollars, 0)
			const fractionOfTotal = value / totalEstimateValue
			const totalFractionOfTimeValue = roundToTwo(totalEstimateValue * fractionOfTotal)

			// for each estimate look for existing estimate, update existing or create new
			const updatedInvoiceEstimates: InvoiceEstimateData = JSON.parse(JSON.stringify(invoiceEstimateData)) // deep copy
			let ongoingTotal = 0
			for (let i = 0; i < updateEstimates.length; i++) {
				const estimateID = updateEstimates[i].estimateID!
				let estimateValue = roundToTwo(updateEstimates[i].remainingDollars * fractionOfTotal)
				ongoingTotal += estimateValue
				// on final value only, if we are a little bit short of where we should be add the difference
				if (ongoingTotal !== totalFractionOfTimeValue && i + 1 === updateEstimates.length) {
					estimateValue += totalFractionOfTimeValue - ongoingTotal
				}

				const invoiceEstimateRecordIndex = updatedInvoiceEstimates.findIndex((ie) => ie.estimateID === estimateID)

				if (invoiceEstimateRecordIndex >= 0) {
					updatedInvoiceEstimates[invoiceEstimateRecordIndex] = { ...updatedInvoiceEstimates[invoiceEstimateRecordIndex], [key]: estimateValue }
				} else {
					updatedInvoiceEstimates.push({ invoiceID: invoiceId, estimateID: estimateID, writtenOff: 0, amountBilled: 0, [key]: estimateValue })
				}
			}
			setInvoiceEstimateData(updatedInvoiceEstimates)
		}
	}

	const handleSave = async () => {
		if (!somethingChanged && invoice.lineItems !== undefined && invoice.lineItems.length > 0) {
			navigate(`/invoice/${invoice.invoiceID}/narration`)
			return
		}
		if (!taxRates || !xeroAccounts || !invoiceTimeData || !invoiceEstimateData || !invoiceCostData || !newEstimates) {
			return
		}
		if (
			invoiceTimeData.filter((time) => time.amountBilled < 0 || time.writtenOff < 0).length > 0 ||
			invoiceEstimateData.filter((estimate) => estimate.amountBilled < 0 || estimate.writtenOff < 0).length > 0 ||
			invoiceCostData.filter((cost) => cost.amountBilled < 0 || cost.writtenOff < 0).length > 0
		) {
			setMessage('Cannot invoice a negative value')
			return
		}
		const updatedInvoiceTimeData = invoiceTimeData.filter((time) => (time.amountBilled ?? null) !== null && (time.writtenOff ?? null) !== null)
		const updatedInvoiceCostData = invoiceCostData.filter((cost) => (cost.amountBilled ?? null) !== null && (cost.writtenOff ?? null) !== null)
		const updatedInvoiceEstimateData = invoiceEstimateData.filter(
			(estimate) => (estimate.amountBilled ?? null) !== null && (estimate.writtenOff ?? null) !== null
		)

		const totalInvoiced = updatedInvoiceTimeData.reduce((total, time) => (total += time.amountBilled), 0) || 0
		const totalEstimated = updatedInvoiceEstimateData.reduce((total, estimate) => (total += estimate.amountBilled), 0) || 0
		const { unitAmount, taxAmount, lineAmount } = getTotalsFromTaxType(
			totalInvoiced - totalEstimated,
			invoice.lineAmountTypes,
			invoice.xeroOrg.version!,
			currentOrgBillySettings?.servicesXeroAccountXeroID,
			xeroAccounts,
			undefined,
			taxRates
		)

		const totalCosted = updatedInvoiceCostData.reduce((total, costs) => (total += costs.amountBilled), 0) || 0
		const costTotals = getTotalsFromTaxType(
			totalCosted,
			invoice.lineAmountTypes,
			invoice.xeroOrg.version!,
			currentOrgBillySettings?.costsXeroAccountXeroID,
			xeroAccounts,
			undefined,
			taxRates
		)

		const totalFromNewEstimates =
			newEstimates.filter((e) => e.originalInvoiceID === invoiceId).reduce((total, estimate) => (total += estimate.value), 0) || 0
		const estimateTotals = getTotalsFromTaxType(
			totalFromNewEstimates,
			invoice.lineAmountTypes,
			invoice.xeroOrg.version!,
			currentOrgBillySettings?.estimatesXeroAccountXeroID,
			xeroAccounts,
			undefined,
			taxRates
		)

		if (unitAmount + costTotals.unitAmount + estimateTotals.unitAmount < 0) {
			setMessage('Cannot invoice a negative value')
			return
		}
		setPageStatus('Submitting')
		// only force to not update in xero if there are no line items with xeroIDs
		const [timesRes, linesRes] = await Promise.all([
			Request.post(`invoice/${invoice.invoiceID}/times`, {
				invoiceTimes: updatedInvoiceTimeData,
				invoiceEstimates: updatedInvoiceEstimateData,
				invoiceCosts: updatedInvoiceCostData,
				newEstimateValue: totalFromNewEstimates,
				writtenUp: writtenUpTotal,
				carryForward: carryForwardTotal,
			}),
			Request.post(`invoice/${invoice.invoiceID}/lines`, {
				triggerQueueOverride: false,
				invoiceLines:
					invoice.lineItems && invoice.lineItems.length > 0
						? invoice.lineItems
						: [
								{
									invoiceID: invoice.invoiceID,
									description: 'Services',
									order: 0 + (invoice.lineItems?.length || 0),
									quantity: 1,
									unitAmount: unitAmount,
									taxAmount: taxAmount,
									lineAmount: lineAmount,
									xeroAccountXeroID: currentOrgBillySettings?.servicesXeroAccountXeroID,
									xeroTaxRateType: xeroAccounts.find((xa) => xa.xeroID === currentOrgBillySettings?.servicesXeroAccountXeroID)?.taxType,
								},
								{
									invoiceID: invoice.invoiceID,
									description: `Service Fee (${currentAccountBillySettings ? currentAccountBillySettings.serviceFee : '2'}%)`,
									order: 1 + (invoice.lineItems?.length || 0),
									quantity: 1,
									unitAmount:
										(unitAmount + (totalFromNewEstimates > 0 ? estimateTotals.unitAmount : 0)) *
										(currentAccountBillySettings ? currentAccountBillySettings.serviceFee / 100 : 0.02),
									taxAmount:
										(taxAmount + (totalFromNewEstimates > 0 ? estimateTotals.taxAmount : 0)) *
										(currentAccountBillySettings ? currentAccountBillySettings.serviceFee / 100 : 0.02),
									lineAmount:
										(lineAmount + (totalFromNewEstimates > 0 ? estimateTotals.lineAmount : 0)) *
										(currentAccountBillySettings ? currentAccountBillySettings.serviceFee / 100 : 0.02),
									xeroAccountXeroID: currentOrgBillySettings?.servicesXeroAccountXeroID,
									xeroTaxRateType: xeroAccounts.find((xa) => xa.xeroID === currentOrgBillySettings?.servicesXeroAccountXeroID)?.taxType,
								},
						  ]
								.concat(
									totalFromNewEstimates > 0
										? [
												{
													invoiceID: invoice.invoiceID,
													description: 'Estimates',
													order: 2 + (invoice.lineItems?.length || 0),
													quantity: 1,
													unitAmount: estimateTotals.unitAmount,
													taxAmount: estimateTotals.taxAmount,
													lineAmount: estimateTotals.lineAmount,
													xeroAccountXeroID: currentOrgBillySettings?.estimatesXeroAccountXeroID,
													xeroTaxRateType: xeroAccounts.find(
														(xa) => xa.xeroID === currentOrgBillySettings?.estimatesXeroAccountXeroID
													)?.taxType,
												},
										  ]
										: []
								)
								.concat(
									costTotals.lineAmount > 0
										? [
												{
													invoiceID: invoice.invoiceID,
													description: 'Costs',
													order: (totalFromNewEstimates > 0 ? 3 : 2) + (invoice.lineItems?.length || 0),
													quantity: 1,
													unitAmount: costTotals.unitAmount,
													taxAmount: costTotals.taxAmount,
													lineAmount: costTotals.lineAmount,
													xeroAccountXeroID: currentOrgBillySettings?.costsXeroAccountXeroID,
													xeroTaxRateType: xeroAccounts.find((xa) => xa.xeroID === currentOrgBillySettings?.costsXeroAccountXeroID)
														?.taxType,
												},
										  ]
										: []
								),
			}),
		])
		setPageStatus('Ready')
		if (timesRes.data.success && linesRes.data.success) {
			navigate(`/invoice/${invoice.invoiceID}/narration`)
		}
	}

	const handleEstimateSubmit = async (estimate: UpdateableEstimate) => {
		setSomethingChanged(true)
		if (!estimate.estimateID) {
			const res = await Request.post(`estimate`, { ...estimate, clientID: invoice.clientID })
			setNewEstimates([...(newEstimates || []), ...res.data.estimates])
			setEstimateModalEstimate(null)
			await getData()
		} else {
			const res = await Request.put(`estimate`, estimate)
			setNewEstimates((newEstimates || []).map((e) => (e.estimateID === estimate.estimateID ? res.data.estimates[0] : e)))
			setEstimateModalEstimate(null)
			await getData()
		}
	}

	const handleEstimateDelete = async (estimateId: string) => {
		setSomethingChanged(true)
		const res = await Request.del(`estimate/${estimateId}?invoiceContext=${invoice.invoiceID}`)
		if (res.data.success) {
			setNewEstimates((newEstimates || []).filter((e) => e.estimateID !== estimateId))
			await getData()
		}
		setEstimateModalEstimate(null)
	}

	const handleAddOtherJob = (jobIDs: string[]) => {
		setPageStatus('Loading')
		getJobWipData(jobIDs)
	}

	const handleApplyGroupingClick = async () => {
		setShowGroupingPanel(false)
		setPageStatus('Loading')
		getWipData(invoice, invoiceTimeData || [], invoiceCostData || [], invoiceEstimateData || [])
	}

	const handleEstimateClick = (estimateId?: string) => {
		const estimate = newEstimates?.find((e) => e.estimateID === estimateId)
		if (estimate) {
			estimate.date = estimate.date.split('T')[0] // Date input field only takes the date part
		}
		setEstimateModalEstimate(estimate || newEstimate(invoiceId))
	}
	const handleAddJobClick = () => {
		setShowJobModal(true)
	}

	const columnWidths = {
		jobType: '130px',
		client: '80px',
		assignment: '120px',
		date: '120px',
		employee: '200px',
		task: '200px',
		comments: '400px',
		agedWip: '140px',
		currentWip: '140px',
		wip: '140px',
		writeOff: '130px',
		invoice: '130px',
		carryForward: '130px',
	} as const

	const state = invoiceState(invoice.status, invoice.sentToContact)
	const canEdit = state === 'draft' || state === 'awaiting-approval'

	return (
		<Container fluid className="allocation-screen">
			<Row className="background-light align-items-center py-3">
				<Col>
					<Row className="pb-3 align-items-center">
						<Col sm="auto">
							<h4 className="mb-0">Invoice Allocation</h4>
						</Col>
						{location.state && location.state.queryString && (
							<Col>
								<Link to={`/invoices${location.state.queryString}`}>Back to Invoices</Link>
							</Col>
						)}
						<Col />
						<Col sm="auto">
							<span>
								Status: <span className="span-bold">{state}</span>
							</span>
						</Col>
					</Row>
					<Row className="align-items-center">
						<Col sm={8}>
							<InvoiceInfoCard invoice={invoice} setInvoice={setInvoice} />
						</Col>
						<Col />
						<Col sm="auto">
							<FormText
								label="Restrict Until"
								value={restrictionDate}
								// onChange={(e) => setRestrictionDate(e.target.value)}
								name="restrictionDate"
								type="date"
								disabled
							/>
						</Col>
						<Col sm="auto">
							<Button
								variant="secondary"
								size="sm"
								onClick={() => {
									setShowGroupingPanel((value) => !value)
								}}
								className={showGroupingPanel ? 'active' : ''}
							>
								<div className="center-flex">
									<span>Grouping</span>
									{showGroupingPanel ? (
										<ChevronUp size={16} strokeWidth={3} className="ml-2" />
									) : (
										<ChevronDown size={16} strokeWidth={3} className="ml-2" />
									)}
								</div>
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>
			{showGroupingPanel && (
				<>
					<Row className="background-light align-items-center py-3">
						<Col>
							{groupingOptions.map((g, i) => (
								<React.Fragment key={g.value}>
									<Row key={g.value} className={i > 0 ? 'pt-2' : ''}>
										<Col>
											<Form.Check
												type="radio"
												id={g.value}
												label={g.label}
												name="grouping"
												checked={grouping === g}
												onChange={() => setGrouping(g)}
											/>
										</Col>
									</Row>
								</React.Fragment>
							))}
						</Col>
						<Col sm="auto" className="center-flex">
							<Button variant="primary" onClick={handleApplyGroupingClick}>
								<div className="center-flex px-3">
									<RefreshCw size={20} className="mr-2" />
									Apply
								</div>
							</Button>
						</Col>
					</Row>
				</>
			)}
			{message && (
				<Row className="mt-3">
					<Col>
						<Alert variant="danger">{message}</Alert>
					</Col>
				</Row>
			)}
			<Row className="allocation-screen-body">
				<Col>
					<ListingScreen
						isLoading={wipData === null || invoice === null}
						wipData={wipData}
						invoiceData={invoice}
						invoiceTimeData={invoiceTimeData}
						invoiceEstimateData={invoiceEstimateData}
						invoiceCostData={invoiceCostData}
						onUpdateTime={handleUpdateInvoiceTime}
						columnWidths={columnWidths}
						handleEstimateClick={handleEstimateClick}
						handleAddJobClick={handleAddJobClick}
						restrictedDate={restrictionDate + 'T00:00:00Z'}
					/>
				</Col>
			</Row>
			<Row className="totals-bottom-panel px-3 pb-3">
				<Col>
					<Row>
						<Col />
						<Col sm="auto" className="p-0">
							<table className="totals-table">
								<tbody>
									{newEstimateTotal > 0 && (
										<tr>
											<td>
												<span className="span-body-2 span-extra span-navy">NEW ESTIMATES:</span>
											</td>
											<td></td>
											<td></td>
											<td></td>
											<td></td>
											<td>
												<FormNumber name="invTotal" value={newEstimateTotal} disabled />
											</td>
											<td></td>
										</tr>
									)}
									<tr>
										<td>
											<span className="span-body-2 span-extra span-navy">TOTALS:</span>
										</td>
										<td width={columnWidths.agedWip}>
											<div className="text-end">{roundToTwo(agedWipTotal || 0).toLocaleString()}</div>
										</td>
										<td width={columnWidths.currentWip}>
											<div className="text-end">{roundToTwo(currentWipTotal || 0).toLocaleString()}</div>
										</td>
										<td width={columnWidths.wip}>
											<div className="text-end">{roundToTwo(wipTotal || 0).toLocaleString()}</div>
										</td>
										<td width={columnWidths.writeOff}>
											<FormNumber name="woTotal" value={roundToTwo(woTotal)} disabled />
										</td>
										<td width={columnWidths.invoice}>
											<FormNumber name="invTotal" value={roundToTwo(invTotal + newEstimateTotal - invEstimateTotal)} disabled />
										</td>
										<td width={columnWidths.carryForward}>
											<div className="text-end">{roundToTwo(carryForwardTotal || 0).toLocaleString()}</div>
										</td>
									</tr>
								</tbody>
							</table>
						</Col>
					</Row>
					<Row className="button-row pt-3 align-items-center">
						<Col />
						<Col sm="auto">
							<Link to="/invoices">Cancel</Link>
						</Col>
						<Col sm="auto">
							<Button
								variant="primary"
								className="bottom-button"
								disabled={wipTotal < 0 || pageStatus !== 'Ready' || !canEdit}
								onClick={handleSave}
							>
								Next
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>
			<OtherJobPanel
				isOpen={showJobModal}
				onClose={() => setShowJobModal(false)}
				currentInvoiceID={invoiceId}
				currentGrouping={grouping}
				currentInvoiceClientGroupID={invoice.clientGroupID || client?.clientGroup?.clientGroupID || ''}
				handleOtherJob={handleAddOtherJob}
			/>
			<NewEstimateModal
				onClose={() => setEstimateModalEstimate(null)}
				estimate={estimateModalEstimate}
				handleNewEstimate={handleEstimateSubmit}
				handleEstimateDelete={handleEstimateDelete}
			/>
		</Container>
	)
}

const isDateBefore = (source: string, target: string) => {
	const utcTarget = target + 'T00:00:00Z'
	return DateTime.fromISO(source).diff(DateTime.fromISO(utcTarget), ['days']).days <= 0
}

const getTimeInClient = (id: string, types: string[], restrictionDate: string) => (client: WipClientWithJobsAndLatestInvoice) =>
	client.jobs.flatMap((job) =>
		job.timeItems.filter(
			(time) =>
				((time.timeID === id && types.includes('time')) ||
					(time.costID === id && types.includes('cost')) ||
					(time.estimateID === id && types.includes('estimate'))) &&
				isDateBefore(time.date, restrictionDate)
		)
	)

const getTimeInWip = (wipData: WipData, id: string, types: string[], restrictionDate: string): WipTimeWithoutJob[] =>
	wipData.grouping === 'fcje'
		? wipData.familyGroups.flatMap((familyGroups) => familyGroups.clients.flatMap(getTimeInClient(id, types, restrictionDate)))
		: wipData.familyGroups.flatMap((familyGroups) =>
				familyGroups.jobTypes.flatMap((jobType) => jobType.clients.flatMap(getTimeInClient(id, types, restrictionDate)))
		  )

const getTimeInClientJob = (id: string, types: string[], restrictionDate: string) => (client: WipClientWithJobsAndLatestInvoice) =>
	client.jobs.flatMap((job) =>
		job.jobID === id
			? job.timeItems.filter(
					(time) =>
						((time.timeID && types.includes('time')) ||
							(time.costID && types.includes('cost')) ||
							(time.estimateID && types.includes('estimate'))) &&
						isDateBefore(time.date, restrictionDate)
			  )
			: []
	)

const getAllTimesForJobInWip = (wipData: WipData, id: string, types: string[], restrictionDate: string): WipTimeWithoutJob[] =>
	wipData.grouping === 'fcje'
		? wipData.familyGroups.flatMap((familyGroups) => familyGroups.clients.flatMap(getTimeInClientJob(id, types, restrictionDate)))
		: wipData.familyGroups.flatMap((familyGroups) =>
				familyGroups.jobTypes.flatMap((jobType) => jobType.clients.flatMap(getTimeInClientJob(id, types, restrictionDate)))
		  )

const getTimeInClientClient = (id: string, types: string[], restrictionDate: string) => (client: WipClientWithJobsAndLatestInvoice) =>
	client.clientID === id
		? client.jobs.flatMap((job) =>
				job.timeItems.filter(
					(time) =>
						((time.timeID && types.includes('time')) ||
							(time.costID && types.includes('cost')) ||
							(time.estimateID && types.includes('estimate'))) &&
						isDateBefore(time.date, restrictionDate)
				)
		  )
		: []

const getAllTimesForClientInWip = (wipData: WipData, id: string, types: string[], restrictionDate: string): WipTimeWithoutJob[] =>
	wipData.grouping === 'fcje'
		? wipData.familyGroups.flatMap((familyGroups) => familyGroups.clients.flatMap(getTimeInClientClient(id, types, restrictionDate)))
		: wipData.familyGroups.flatMap((familyGroups) =>
				familyGroups.jobTypes.flatMap((jobType) => jobType.clients.flatMap(getTimeInClientClient(id, types, restrictionDate)))
		  )

const getAllTimesForJobTypeInWip = (wipData: WipData, id: string, types: string[], restrictionDate: string): WipTimeWithoutJob[] =>
	wipData.grouping === 'fjcj'
		? wipData.familyGroups.flatMap((familyGroups) =>
				familyGroups.jobTypes.flatMap((jobType) =>
					jobType.type === id
						? jobType.clients.flatMap((client) =>
								client.jobs.flatMap((job) =>
									job.timeItems.filter(
										(time) =>
											((time.timeID && types.includes('time')) ||
												(time.costID && types.includes('cost')) ||
												(time.estimateID && types.includes('estimate'))) &&
											isDateBefore(time.date, restrictionDate)
									)
								)
						  )
						: []
				)
		  )
		: []

const getAllTimes = (wipData: WipData): WipTimeWithoutJob[] =>
	wipData.grouping === 'fcje'
		? wipData.familyGroups.flatMap((familyGroups) => familyGroups.clients.flatMap((client) => client.jobs.flatMap((job) => job.timeItems)))
		: wipData.familyGroups.flatMap((familyGroups) =>
				familyGroups.jobTypes.flatMap((jobType) => jobType.clients.flatMap((client) => client.jobs.flatMap((job) => job.timeItems)))
		  )

// TODO: unshitify
const mergeWipData = (wipDataArray: WipData[]): WipData => {
	if (wipDataArray.length < 1) {
		throw new Error('Need at least 1 wip data sets to merge')
	}
	if (wipDataArray.length === 1) {
		return wipDataArray[0] // nothing to merge
	}
	if (wipDataArray.some((wipData, _index, array) => wipData.grouping !== array[0].grouping)) {
		throw new Error('Not all wip data of same grouping')
	}

	return wipDataArray.reduce(
		(merged, current) => {
			if (merged.grouping === 'fcje' && current.grouping === 'fcje') {
				for (let i = 0; i < current.familyGroups.length; i++) {
					const currentFamilyGroup = current.familyGroups[i]
					const familyGroupIndex = merged.familyGroups.findIndex((fg) => fg.clientGroupID === currentFamilyGroup.clientGroupID)
					if (familyGroupIndex < 0) {
						merged.familyGroups.push(currentFamilyGroup)
					} else {
						for (let j = 0; j < currentFamilyGroup.clients.length; j++) {
							const currentClient = currentFamilyGroup.clients[j]
							const clientIndex = merged.familyGroups[familyGroupIndex].clients.findIndex((c) => c.clientID === currentClient.clientID)
							if (clientIndex < 0) {
								merged.familyGroups[familyGroupIndex].clients.push(currentClient)
							} else {
								for (let k = 0; k < currentClient.jobs.length; k++) {
									const currentJob = currentClient.jobs[k]
									const jobIndex = merged.familyGroups[familyGroupIndex].clients[clientIndex].jobs.findIndex(
										(j) => j.jobID === currentJob.jobID
									)
									if (jobIndex < 0) {
										merged.familyGroups[familyGroupIndex].clients[clientIndex].jobs.push(currentJob)
									} else {
										for (let l = 0; l < currentJob.timeItems.length; l++) {
											const currentTime = currentJob.timeItems[l]
											const timeIndex = merged.familyGroups[familyGroupIndex].clients[clientIndex].jobs[jobIndex].timeItems.findIndex(
												(t) => t.timeID === currentTime.timeID
											)
											if (timeIndex < 0) {
												merged.familyGroups[familyGroupIndex].clients[clientIndex].jobs[jobIndex].timeItems.push(currentTime)
											} else {
												// ignore it, we already have it
											}
										}
									}
								}
							}
						}
					}
				}
			} else if (merged.grouping === 'fjcj' && current.grouping === 'fjcj') {
				for (let h = 0; h < current.familyGroups.length; h++) {
					const currentFamilyGroup = current.familyGroups[h]
					const familyGroupIndex = merged.familyGroups.findIndex((fg) => fg.clientGroupID === currentFamilyGroup.clientGroupID)
					if (familyGroupIndex < 0) {
						merged.familyGroups.push(currentFamilyGroup)
					} else {
						for (let i = 0; i < currentFamilyGroup.jobTypes.length; i++) {
							const currentJobType = currentFamilyGroup.jobTypes[i]
							const jobTypeIndex = merged.familyGroups[familyGroupIndex].jobTypes.findIndex((jt) => jt.type === currentJobType.type)
							if (jobTypeIndex < 0) {
								merged.familyGroups[familyGroupIndex].jobTypes.push(currentJobType)
							} else {
								for (let j = 0; j < currentJobType.clients.length; j++) {
									const currentClient = currentJobType.clients[j]
									const clientIndex = merged.familyGroups[familyGroupIndex].jobTypes[jobTypeIndex].clients.findIndex(
										(c) => c.clientID === currentClient.clientID
									)
									if (clientIndex < 0) {
										merged.familyGroups[familyGroupIndex].jobTypes[jobTypeIndex].clients.push(currentClient)
									} else {
										for (let k = 0; k < currentClient.jobs.length; k++) {
											const currentJob = currentClient.jobs[k]
											const jobIndex = merged.familyGroups[familyGroupIndex].jobTypes[jobTypeIndex].clients[clientIndex].jobs.findIndex(
												(j) => j.jobID === currentJob.jobID
											)
											if (jobIndex < 0) {
												merged.familyGroups[familyGroupIndex].jobTypes[jobTypeIndex].clients[clientIndex].jobs.push(currentJob)
											} else {
												for (let l = 0; l < currentJob.timeItems.length; l++) {
													const currentTime = currentJob.timeItems[l]
													const timeIndex = merged.familyGroups[familyGroupIndex].jobTypes[jobTypeIndex].clients[clientIndex].jobs[
														jobIndex
													].timeItems.findIndex((t) => t.timeID === currentTime.timeID)
													if (timeIndex < 0) {
														merged.familyGroups[familyGroupIndex].jobTypes[jobTypeIndex].clients[clientIndex].jobs[
															jobIndex
														].timeItems.push(currentTime)
													} else {
														// ignore it, we already have it
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}

			return merged
		},
		{ familyGroups: [], grouping: wipDataArray[0].grouping }
	)
}

export const getTotalsFromTaxType = (
	amount: number,
	invoiceTaxType: InvoiceLineAmountTypes,
	xeroOrgVersion: string,
	xeroAccountXeroID: string | undefined,
	xeroAccounts: XeroAccount[],
	xeroTaxRateType: string | undefined,
	xeroTaxRates: XeroTaxRate[]
) => {
	const xeroAccount = xeroAccounts.find((xa) => xa.xeroID === xeroAccountXeroID)
	const taxRate = getTaxRateFromType(xeroTaxRateType || xeroAccount?.taxType, xeroOrgVersion, xeroTaxRates)

	if (!xeroAccount || taxRate.label === '') {
		throw new Error(`Can't find correct tax rate for version: ${xeroOrgVersion}, account: ${xeroAccountXeroID}`)
	}

	if (invoiceTaxType === 'Inclusive') {
		return {
			unitAmount: amount,
			taxAmount: amount - amount / (1 + Number(taxRate.taxRate)),
			lineAmount: amount,
		}
	} else if (invoiceTaxType === 'Exclusive') {
		return {
			unitAmount: amount,
			taxAmount: amount * Number(taxRate.taxRate),
			lineAmount: amount * (1 + Number(taxRate.taxRate)),
		}
	} else {
		// (invoiceTaxType === 'NoTax')
		return {
			unitAmount: amount,
			taxAmount: 0,
			lineAmount: amount,
		}
	}
}

export default InvoiceDetail
